/**
 * Iter-170 · Route-level guard for portal feature toggles.
 *
 * Wraps a route element. If the current org has toggled off `feature`, the
 * component:
 *   1. Renders a friendly "This feature is disabled" screen (not the target
 *      route content) so URL-poking users cannot bypass sidebar hiding.
 *   2. Redirects to /app/dashboard after 3 seconds with a toast, so the user
 *      isn't stranded on a dead-end.
 *
 * Reads the effective toggle map from GET /industry-features (same source the
 * Sidebar uses). Listens for the `feature-toggles-updated` window event so a
 * toggle save in the admin panel takes effect immediately without a reload.
 */
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Lock } from "lucide-react";
import { Button } from "@/components/ui/button";

export default function FeatureGate({ feature, children }) {
  const [toggles, setToggles] = useState(null);   // null = still loading
  const nav = useNavigate();

  useEffect(() => {
    let cancelled = false;
    const load = () => api.get("/industry-features").then(r => {
      if (!cancelled) setToggles(r.data?.portal_feature_toggles || {});
    }).catch(() => !cancelled && setToggles({}));
    load();
    const onUpdate = () => load();
    window.addEventListener("feature-toggles-updated", onUpdate);
    return () => { cancelled = true; window.removeEventListener("feature-toggles-updated", onUpdate); };
  }, []);

  const enabled = toggles === null ? true : toggles[feature] !== false;

  useEffect(() => {
    if (toggles !== null && !enabled) {
      toast.error(`"${feature}" is disabled by your admin — redirecting home`);
      const t = setTimeout(() => nav("/app/dashboard"), 3000);
      return () => clearTimeout(t);
    }
  }, [toggles, enabled, feature, nav]);

  if (toggles === null) return null;              // silent while loading

  if (!enabled) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center p-8"
             data-testid={`feature-gate-blocked-${feature}`}>
        <div className="text-center max-w-md">
          <div className="w-16 h-16 rounded-sm bg-red-500/10 flex items-center justify-center mx-auto mb-4">
            <Lock className="w-7 h-7 text-red-500" />
          </div>
          <h2 className="font-heading text-2xl mb-2">Feature disabled</h2>
          <p className="text-sm text-muted-foreground mb-6">
            The <b className="font-mono text-foreground">{feature}</b> feature has been switched off
            by your Business Owner / Admin. Ask them to re-enable it under
            Admin → Portal Features.
          </p>
          <Button onClick={() => nav("/app/dashboard")}
                    className="rounded-sm gap-1.5" data-testid="feature-gate-home">
            Go to Dashboard
          </Button>
        </div>
      </div>
    );
  }
  return children;
}
